/* ============================================================
   KROQ MATCH — moteur de correspondance

   Fonctions pures, aucun accès au DOM, aucun appel réseau. Le même
   fichier tourne dans le navigateur (window.KroqMatchEngine) et sous
   Node, ce qui permet de le tester sans navigateur.

   ── Principe ────────────────────────────────────────────────
   Un score n'est calculé que sur les critères où LES DEUX côtés ont
   une donnée. Un produit renseigné sur un seul critère n'atteint donc
   jamais 100 % : la part des critères réellement évalués est rendue
   séparément, sous le nom de `confidence`.

   Aucune valeur n'est inventée. Une donnée absente retire son critère
   du calcul ; elle ne vaut jamais zéro, ni une moyenne.

   ── Costaud Kroq ────────────────────────────────────────────
   `custom.chew_resistance` est la seule source de vérité. La collection
   « Les Costauds Kroq » ne sert que de relais pendant la migration :
   tant que le métachamp est vide, l'appartenance à la collection fait
   présumer 4/5, ce qui est signalé (`assumed: true`) et coûte de la
   confiance. Dès que le métachamp existe, il l'emporte, et une
   incohérence entre les deux est remontée aux éditeurs — jamais au
   client.

   Aucun jouet pour chien n'est indestructible : le moteur ne produit
   aucune formulation qui le laisserait croire.
   ============================================================ */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.KroqMatchEngine = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ── Seuils publiés par la boutique ──
     Repris tels quels de la page d'accueil et des tuiles de taille :
     ce sont les seuils que le site annonce déjà aux clients, pas une
     grille inventée pour le moteur. */
  var SIZE_BANDS = [
    { key: 'xs', min: 0, max: 5 },
    { key: 's', min: 5, max: 10 },
    { key: 'm', min: 10, max: 20 },
    { key: 'l', min: 20, max: 35 },
    { key: 'xl', min: 35, max: Infinity }
  ];

  /* Chiot 2 à 12 mois · junior 12 à 24 · adulte 2 à 7 ans · senior 7 ans et + */
  var AGE_BANDS = [
    { key: 'chiot', min: 0, max: 12 },
    { key: 'junior', min: 12, max: 24 },
    { key: 'adulte', min: 24, max: 84 },
    { key: 'senior', min: 84, max: Infinity }
  ];

  /* ── Normalisation des libellés ──
     Les races portent des libellés français saisis à la main. Une seule
     table, ici, plutôt que des comparaisons de chaînes dispersées. */
  var LEVELS = {
    'faible': 1.5,
    'modere': 2.5,
    'modere a eleve': 3.25,
    'eleve': 4,
    'tres eleve': 5
  };

  var DOG_ENERGY = { calme: 1, modere: 2.5, actif: 4, 'tres actif': 5 };
  var DOG_CHEW = { doux: 2, normal: 3, fort: 4, 'tres fort': 5 };

  function deburr(s) {
    return String(s == null ? '' : s)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function levelFromLabel(label) {
    var k = deburr(label);
    if (!k) return null;
    if (LEVELS[k] != null) return LEVELS[k];
    /* « Modéré à élevé » et ses variantes d'orthographe. */
    if (/modere.*eleve/.test(k)) return 3.25;
    if (/tres.*eleve/.test(k)) return 5;
    if (/eleve/.test(k)) return 4;
    if (/modere|moyen/.test(k)) return 2.5;
    if (/faible|bas/.test(k)) return 1.5;
    return null;
  }

  /* « 25 à 40 kg », « 1,5 à 3 kg », « 20 à 50 kg selon variété » */
  function parseWeightRange(text) {
    var t = deburr(text).replace(/,/g, '.');
    var nums = t.match(/\d+(?:\.\d+)?/g);
    if (!nums || !nums.length) return null;
    var a = parseFloat(nums[0]);
    var b = nums.length > 1 ? parseFloat(nums[1]) : a;
    return { min: Math.min(a, b), max: Math.max(a, b) };
  }

  function sizeFromWeight(kg) {
    if (kg == null || !isFinite(kg) || kg <= 0) return null;
    for (var i = 0; i < SIZE_BANDS.length; i++) {
      if (kg >= SIZE_BANDS[i].min && kg < SIZE_BANDS[i].max) return SIZE_BANDS[i].key;
    }
    return 'xl';
  }

  function lifeStageFromMonths(months) {
    if (months == null || !isFinite(months) || months < 0) return null;
    for (var i = 0; i < AGE_BANDS.length; i++) {
      if (months >= AGE_BANDS[i].min && months < AGE_BANDS[i].max) return AGE_BANDS[i].key;
    }
    return 'senior';
  }

  /* ── Profil du chien ──
     Le profil saisi par le maître prime toujours sur la moyenne de la
     race : celle-ci ne sert qu'à pré-remplir un champ laissé vide. */
  function normalizeDogProfile(input, breed) {
    input = input || {};
    var weight = num(input.weightKg);
    var months = num(input.ageMonths);

    var energy = DOG_ENERGY[deburr(input.energy)];
    if (energy == null) energy = num(input.energy);
    var chew = DOG_CHEW[deburr(input.chew)];
    if (chew == null) chew = num(input.chew);

    var fromBreed = [];
    if (breed) {
      if (energy == null && breed.energy != null) { energy = breed.energy; fromBreed.push('energy'); }
      if (chew == null && breed.chew != null) { chew = breed.chew; fromBreed.push('chew'); }
      if (weight == null && breed.weightMin != null) {
        weight = (breed.weightMin + breed.weightMax) / 2;
        fromBreed.push('weight');
      }
    }

    return {
      /* Le prénom ne pèse sur aucun calcul. Il change ce que le maître
         lit — « les matchs de Nox » plutôt que « vos résultats ». */
      name: (input.name || '').toString().trim().slice(0, 30) || null,
      /* Un chien souvent laissé seul avec ses jouets écarte ceux qui
         demandent une présence. La consigne de surveillance passe alors
         du statut d'information à celui de critère — mais en pénalité
         douce, jamais en exclusion : c'est un usage à encadrer, pas un
         danger avéré. */
      playsAlone: !!input.playsAlone,
      breed: input.breed || null,
      weightKg: weight,
      size: sizeFromWeight(weight),
      ageMonths: months,
      lifeStage: lifeStageFromMonths(months),
      energy: clamp(energy, 1, 5),
      chew: clamp(chew, 1, 5),
      activities: Array.isArray(input.activities) ? input.activities.map(deburr).filter(Boolean) : [],
      derivedFromBreed: fromBreed
    };
  }

  function normalizeBreed(raw) {
    if (!raw) return null;
    var w = parseWeightRange(raw.poids);
    return {
      handle: raw.handle || null,
      name: raw.name || null,
      energy: levelFromLabel(raw.energy),
      chew: levelFromLabel(raw.chew),
      weightMin: w ? w.min : null,
      weightMax: w ? w.max : null,
      recommended: Array.isArray(raw.recommended) ? raw.recommended : []
    };
  }

  /* ── Profil produit ──
     `chewResistance` vient du métachamp. À défaut, et seulement pendant
     la migration, l'appartenance à « Les Costauds Kroq » fait présumer
     4/5 — marqué comme présumé, et payé en confiance. */
  function normalizeProduct(raw) {
    raw = raw || {};
    var declared = num(raw.chewResistance);
    var chew = clamp(declared, 1, 5);
    var assumed = false;

    if (chew == null && raw.isCostaud) {
      chew = 4;
      assumed = true;
    }

    /* Incohérence de données, à l'usage des éditeurs uniquement. */
    var inconsistent = false;
    if (declared != null) {
      if (raw.isCostaud && declared < 4) inconsistent = 'costaud_mais_resistance_faible';
      else if (!raw.isCostaud && declared >= 4) inconsistent = 'resistance_elevee_hors_costaud';
    }

    /* Intensité : déclarée par `custom.activity_intensity`, désormais
       renseignée partout. L'approximation par les collections reste en
       repli pour un produit créé sans le champ. */
    var intensity = clamp(num(raw.intensity), 1, 5);
    var intensityAssumed = false;
    if (intensity == null) {
      var acts = (raw.activities || []).map(deburr);
      if (acts.indexOf('a-lexterieur') !== -1) { intensity = 4; intensityAssumed = true; }
      else if (acts.indexOf('pour-la-maison') !== -1) { intensity = 2; intensityAssumed = true; }
    }

    /* `play_types` est déclaré en `single_line_text_field` et non en
       liste : un produit n'en porte qu'un. On accepte les deux formes
       pour n'avoir rien à changer le jour où la définition passera en
       liste. */
    var playTypes = raw.playTypes;
    if (typeof playTypes === 'string') playTypes = playTypes ? [playTypes] : [];
    if (!Array.isArray(playTypes)) playTypes = [];

    var primaryUses = raw.primaryUses;
    if (typeof primaryUses === 'string') primaryUses = primaryUses ? [primaryUses] : [];
    if (!Array.isArray(primaryUses)) primaryUses = [];

    /* Une plage de poids n'a de sens que complète et ordonnée. Une
       borne seule ou inversée est ignorée plutôt que devinée. */
    var wMin = num(raw.weightMin);
    var wMax = num(raw.weightMax);
    if (wMin == null || wMax == null || wMin > wMax) { wMin = null; wMax = null; }

    return {
      id: raw.id,
      handle: raw.handle,
      title: raw.title,
      url: raw.url,
      image: raw.image || null,
      price: raw.price != null ? raw.price : null,
      type: raw.type || null,
      isToy: deburr(raw.type) === 'jouet' || (!raw.type && !!(raw.playTypes || []).length),
      sizes: (raw.sizes || []).map(deburr),
      ages: (raw.ages || []).map(deburr),
      activities: (raw.activities || []).map(deburr),
      playTypes: playTypes.map(deburr).filter(Boolean),
      primaryUses: primaryUses.map(deburr).filter(Boolean),
      weightMin: wMin,
      weightMax: wMax,
      chewResistance: chew,
      chewAssumed: assumed,
      intensity: intensity,
      intensityAssumed: intensityAssumed,
      /* Hors du score : départage les ex æquo et nourrit l'explication.
         Corrélé à 0,76 avec la mastication, il ferait double emploi. */
      durability: clamp(num(raw.durability), 1, 5),
      /* Hors du score également : une consigne de sécurité ne rend pas
         un produit plus pertinent. Elle s'affiche, elle ne note pas. */
      supervision: clamp(num(raw.supervision), 1, 3),
      enrichment: clamp(num(raw.enrichment), 1, 5),
      isCostaud: !!raw.isCostaud,
      dataInconsistency: inconsistent,
      /* Renseigné par `attachBreedRecommendations` : hors d'une race
         donnée, la question n'a pas de sens et le critère est écarté. */
      recommendedForBreed: null,
      packComponents: raw.packComponents || []
    };
  }

  /* La race recommande une liste de produits. On marque chaque produit
     plutôt que de chercher dans la liste à chaque calcul. */
  function attachBreedRecommendations(products, breed) {
    var ids = {};
    if (breed && breed.recommended) {
      breed.recommended.forEach(function (r) { ids[String(r)] = true; });
    }
    var known = Object.keys(ids).length > 0;
    products.forEach(function (p) {
      p.recommendedForBreed = known ? !!(ids[String(p.id)] || ids[String(p.handle)]) : null;
    });
    return products;
  }

  /* ── Poids des critères — révisés sur données réelles ──

     Relevé du 12 août 2026 sur les 23 produits, entropie normalisée :

       activity_intensity  0,95   meilleur discriminant
       supervision_level   0,90   information, pas score
       play_types          0,88   mais UNE valeur par produit
       chew_resistance     0,88   sur 10 produits seulement
       durability_score    0,82   corrélé à chew (r = 0,76)
       enrichment_level    0,63   17 produits sur 23 valent 1

     Deux constats ont déplacé les poids :

     · `dog_weight_min/max` est très permissif — un chien de 3 kg est
       compatible avec 21 produits sur 23, un de 45 kg avec 17. Le
       gabarit reste vital comme EXCLUSION, mais il ne discrimine
       presque plus comme score : son poids baisse de 30 à 15.

     · `primary_use` est une vraie liste, remplie sur 23/23, avec onze
       termes et plusieurs valeurs par produit. C'est désormais le
       signal d'usage le plus riche du catalogue : l'axe usage monte
       de 15 à 24.

     `durability_score` n'entre pas au score : corrélé à 0,76 avec la
     mastication, il ferait double emploi. Il départage les ex æquo et
     nourrit l'explication. `supervision_level` n'entre pas non plus :
     c'est une consigne de sécurité, pas un argument de pertinence. */
  var WEIGHTS = { size: 15, chew: 22, usage: 24, intensity: 14, age: 10, enrichment: 8, breed: 7 };

  /* Ce que le maître coche → le vocabulaire des produits. Les deux
     taxonomies ne se recouvrent pas terme à terme : cette table est le
     seul endroit où la correspondance est écrite. */
  var NEED_TO_USE = {
    /* Ce qu'il aime faire. */
    macher: ['mastication'],
    rapporter: ['lancer_rapporter'],
    tirer: ['traction'],
    reflechir: ['enrichissement', 'occupation'],
    eau: ['aquatique'],
    interieur: ['occupation', 'confort'],
    /* Ce dont le maître a besoin au quotidien. Sans ces quatre entrées,
       douze produits sur vingt-trois portaient un usage qu'aucune
       réponse ne pouvait atteindre — la moitié du catalogue restait
       hors de portée du questionnaire. */
    promenade: ['promenade'],
    securite: ['securite'],
    repas: ['alimentation'],
    soin: ['soin']
  };

  /* Besoins qui relèvent du jeu. Hors de cette liste, un accessoire
     peut légitimement être une bonne réponse. */
  var PLAY_NEEDS = ['macher', 'rapporter', 'tirer', 'reflechir'];

  /* Le dernier palier ne dit pas « moins adapté ». Une laisse, une
     gourde, un tapis rafraîchissant ne sont pas mal assortis à un
     chien : ce sont des catégories valables pour tous, qui ne
     répondent simplement pas à ce qui vient d'être demandé. Un produit
     qui passe tous les critères applicables est compatible, et le dire
     autrement serait un jugement que la donnée ne porte pas.

     Ce qui mérite une réserve — un écart de mastication, une tranche
     d'âge différente — passe par `warnings`, qui nomme le motif. */
  var LEVEL_LABELS = [
    { min: 90, key: 'excellent', label: 'Excellent match' },
    { min: 75, key: 'tres_bon', label: 'Très bon match' },
    { min: 60, key: 'bon', label: 'Bon match' },
    { min: 0, key: 'compatible', label: 'Compatible avec son profil' }
  ];

  function levelFor(score) {
    for (var i = 0; i < LEVEL_LABELS.length; i++) {
      if (score >= LEVEL_LABELS[i].min) return LEVEL_LABELS[i];
    }
    return LEVEL_LABELS[LEVEL_LABELS.length - 1];
  }

  /* ── Trois notions distinctes ────────────────────────────
       rawScore     ce qui classe          — interne
       confidence   la qualité des données — interne
       presentation ce que le client peut légitimement lire

     Un pourcentage est une affirmation. On ne l'écrit que si les
     données la soutiennent : au moins 80 % des critères évalués, et
     aucune valeur présumée dans le calcul. En dessous, le niveau
     qualitatif suffit — il dit la même chose sans prétendre à une
     précision que la donnée n'a pas.

     Une valeur présumée depuis la collection Costauds sert donc au
     classement, jamais à produire un chiffre affiché. */
  function presentationFor(rawScore, confidence, level, assumed, excluded) {
    if (rawScore == null || excluded) {
      return { mode: 'none', publicScore: null, label: null };
    }
    if (confidence >= 0.8 && !assumed) {
      return { mode: 'score', publicScore: rawScore, label: level.label };
    }
    if (confidence >= 0.6) {
      return { mode: 'level', publicScore: null, label: level.label };
    }
    if (rawScore >= 60) {
      return { mode: 'soft', publicScore: null, label: 'Compatible avec son profil' };
    }
    return { mode: 'none', publicScore: null, label: null };
  }

  /**
   * calculateMatch(dog, product) — fonction pure.
   * Retourne { score, level, confidence, matched, warnings, exclusions }.
   * `score` vaut null si aucun critère commun n'est évaluable : on ne
   * classe alors pas le produit plutôt que d'afficher un chiffre creux.
   */
  function calculateMatch(dog, product) {
    var matched = [];
    var warnings = [];
    var exclusions = [];
    var got = 0;   /* poids des critères réellement évalués */
    var earned = 0;
    var soloPenalty = 1;

    function score(key, fit, why) {
      var w = WEIGHTS[key];
      got += w;
      earned += w * fit;
      if (fit >= 0.75 && why) matched.push(why);
    }

    /* 1. Gabarit — contrainte bloquante, et source explicite d'abord.
          La plage produit prime sur les collections : c'est une donnée
          saisie, pas une déduction. Hors plage, le produit n'est pas
          « peu adapté », il ne convient pas.

          Le score ne vaut pas 1 dès qu'on est dans la plage : un chien
          au bord d'une plage 3–50 kg est moins bien servi qu'un chien
          en son milieu. C'est le peu de signal gradué qu'on peut tirer
          de bornes aussi larges. */
    if (dog.weightKg != null && product.weightMin != null) {
      if (dog.weightKg < product.weightMin || dog.weightKg > product.weightMax) {
        exclusions.push({
          code: 'poids',
          message: 'Prévu pour les chiens de ' + product.weightMin + ' à ' + product.weightMax + ' kg.'
        });
        score('size', 0);
      } else {
        var span = product.weightMax - product.weightMin;
        var centrality = span > 0
          ? 1 - Math.abs(dog.weightKg - (product.weightMin + product.weightMax) / 2) / (span / 2)
          : 1;
        score('size', 0.6 + 0.4 * centrality, 'adapté à son poids');
      }
    } else if (dog.size && product.sizes.length) {
      /* Repli sur les collections quand la plage n'est pas saisie. */
      if (product.sizes.indexOf(dog.size) !== -1) {
        score('size', 1, 'adapté à son gabarit');
      } else {
        exclusions.push({
          code: 'taille',
          message: 'Ce produit n’est pas proposé pour un chien de ce gabarit.'
        });
        score('size', 0);
      }
    }

    /* 2. Mastication — deuxième critère, et second garde-fou.
          Un écart de trois niveaux entre la puissance du chien et la
          résistance du produit est un risque, pas une préférence. */
    if (dog.chew != null && product.chewResistance != null) {
      var gap = dog.chew - product.chewResistance;
      if (gap >= 3) {
        exclusions.push({
          code: 'mastication',
          message: 'Résistance trop faible pour sa façon de mâcher.'
        });
        score('chew', 0);
      } else if (gap >= 1.5) {
        warnings.push('Sa mastication est plus forte que ce que ce produit encaisse habituellement.');
        score('chew', 0.25);
      } else if (gap >= 0.5) {
        score('chew', 0.7);
      } else {
        /* Une résistance seulement présumée depuis la collection sert au
           score — c'est le relais de migration accepté — mais elle ne se
           dit pas au client. Annoncer « résistance adaptée » sur une
           valeur devinée serait une affirmation de trop. */
        /* L'explication décrit l'ACCORD, pas le produit : annoncer une
           « résistance adaptée aux mâcheurs appuyés » à qui a déclaré
           une mastication douce ne lui apprend rien sur son chien. */
        score('chew', 1, product.chewAssumed
          ? null
          : (dog.chew >= 4 && product.chewResistance >= 4
            ? 'résistance adaptée aux mâcheurs appuyés'
            : 'résistance cohérente avec sa mastication'));
      }
    }

    /* 3. Usage — `primary_use` d'abord, `play_types` en appoint.

          La mesure est la part des besoins DU CHIEN couverte, jamais le
          nombre d'étiquettes du produit. Un produit à un seul usage qui
          répond au seul besoin exprimé obtient 1 ; un produit à quatre
          usages qui n'en couvre qu'un sur deux obtient 0,5. Porter
          beaucoup d'étiquettes ne rapporte rien.

          `primary_use` dit la fonction, `play_types` le contexte : une
          correspondance de fonction vaut plus qu'une correspondance de
          contexte. */
    if (dog.activities.length && (product.primaryUses.length || product.playTypes.length)) {
      var covered = 0;
      var byUse = [];
      dog.activities.forEach(function (need) {
        var uses = NEED_TO_USE[need] || [];
        var hitUse = uses.some(function (u) { return product.primaryUses.indexOf(u) !== -1; });
        var hitPlay = product.playTypes.indexOf(need) !== -1;
        if (hitUse) { covered += 1; byUse.push(need); }
        else if (hitPlay) { covered += 0.6; }
      });
      var fit = Math.min(1, covered / dog.activities.length);
      score('usage', fit, fit >= 0.75 ? 'correspond à ce qu’il aime faire' : null);
      if (fit === 0) warnings.push('Ne correspond pas aux activités que vous avez indiquées.');
    }

    /* 3 bis. Enrichissement — un bonus quand le besoin est exprimé, et
          rien du tout sinon. Ne pas cocher « réfléchir » n'est pas un
          défaut de compatibilité : c'est une absence de préférence, et
          elle ne doit pénaliser aucun frisbee. */
    var wantsMental = dog.activities.indexOf('reflechir') !== -1;
    if (wantsMental && product.enrichment != null) {
      score('enrichment', (product.enrichment - 1) / 4,
        product.enrichment >= 4 ? 'occupe vraiment la tête' : null);
    }

    /* 4. Âge. */
    if (dog.lifeStage && product.ages.length) {
      var stage = dog.lifeStage === 'chiot' ? 'chiots' : dog.lifeStage;
      var okAge = product.ages.indexOf(stage) !== -1 || product.ages.indexOf(dog.lifeStage) !== -1;
      score('age', okAge ? 1 : 0.2, okAge ? 'convient à son âge' : null);
      if (!okAge) warnings.push('Ce produit vise une autre tranche d’âge.');
    }

    /* 5. Intensité physique — courbe douce, jamais une exclusion.
          Un écart d'un niveau reste très bon : le maître estime
          l'énergie de son chien, il ne la mesure pas. */
    if (dog.energy != null && product.intensity != null) {
      var d = Math.abs(dog.energy - product.intensity);
      var curve = d < 0.5 ? 1 : d < 1.5 ? 0.85 : d < 2.5 ? 0.55 : 0.25;
      score('intensity', curve, d < 1.5 ? 'au niveau de son énergie' : null);
    }

    /* 6. Race — signal de pertinence, jamais de sécurité. Il ne peut
          pas rattraper une exclusion : c'est la règle « sécurité avant
          profil, profil avant préférence, préférence avant race ». */
    if (dog.breed && product.recommendedForBreed != null) {
      score('breed', product.recommendedForBreed ? 1 : 0.4,
        product.recommendedForBreed ? 'recommandé pour sa race' : null);
    }

    /* Consigne de surveillance. Elle n'ajoute jamais de points, et le
       niveau 1 ne signifie jamais « peut rester seul avec ». Elle ne
       retire des points que si le maître a dit que son chien joue
       souvent seul : un jeu qui demande une présence lui conviendra
       mal. Pénalité douce, appliquée hors du dénominateur des critères
       pour ne pas déformer la confiance. */
    if (product.supervision === 3) {
      warnings.push('Jeu à partager : à utiliser sous votre surveillance.');
      if (dog.playsAlone) {
        soloPenalty = 0.85;
        warnings.push('Vous avez indiqué qu’il joue souvent seul : ce produit demande votre présence.');
      }
    }

    if (got === 0) {
      return {
        rawScore: null, level: null, confidence: 0, assumed: false,
        presentation: { mode: 'none', publicScore: null, label: null },
        matched: [], warnings: warnings, exclusions: exclusions
      };
    }

    var raw = (earned / got) * 100;
    var confidence = got / 100;

    /* La confiance ne gonfle pas le score, elle le retient : un produit
       jugé sur un seul critère ne peut pas prétendre à l'excellence. */
    var capped = Math.round(raw * (0.7 + 0.3 * confidence) * soloPenalty);
    if (product.chewAssumed) confidence = Math.max(0, confidence - 0.1);

    var rawScore = exclusions.length ? Math.min(capped, 35) : capped;
    var level = levelFor(rawScore);
    confidence = Math.round(confidence * 100) / 100;
    var assumed = !!product.chewAssumed;

    var pres = presentationFor(rawScore, confidence, level, assumed, exclusions.length > 0);

    return {
      rawScore: rawScore,
      /* `score` = ce que le client peut voir, `rawScore` = ce qui classe.
         Les deux ne coïncident que si la donnée soutient le chiffre. */
      score: pres.publicScore,
      level: level,
      confidence: confidence,
      assumed: assumed,
      excluded: exclusions.length > 0,
      exclusionReasons: exclusions.map(function (e) { return e.message; }),
      matchedCriteria: matched,
      explanations: matched,
      presentation: pres,
      matched: matched,
      warnings: warnings,
      exclusions: exclusions
    };
  }

  /**
   * rankProducts — trie et sépare ce qui est recommandable de ce qui
   * ne l'est pas. Un produit exclu pour raison de sécurité ne remonte
   * jamais dans les meilleurs matchs.
   */
  function rankProducts(dog, products) {
    var scored = products.map(function (p) {
      return { product: p, match: calculateMatch(dog, p) };
    });

    /* Un accessoire n'est pas d'office secondaire : tout dépend de ce
       que le maître a demandé. Si le besoin exprimé relève du jeu, une
       laisse n'a rien à faire en tête — mais si le besoin est l'eau, un
       gilet de flottaison est une excellente réponse.

       La règle regarde donc le CONTEXTE, pas seulement le type. */
    var playNeeds = (dog.activities || []).filter(function (a) {
      return PLAY_NEEDS.indexOf(a) !== -1;
    });

    function isRecommendable(r) {
      var p = r.product;
      if (!p.type) return true;
      if (p.isToy) return true;
      /* Aucun besoin de jeu exprimé : rien ne justifie de reléguer. */
      if (!playNeeds.length) return true;
      /* Besoin de jeu exprimé : l'accessoire doit répondre à un besoin
         réellement formulé pour rester en tête. */
      return (dog.activities || []).some(function (need) {
        return (NEED_TO_USE[need] || []).some(function (u) {
          return p.primaryUses.indexOf(u) !== -1;
        });
      });
    }

    var isPack = function (r) { return deburr(r.product.type) === 'pack'; };

    var live = scored.filter(function (r) {
      return r.match.rawScore != null && !r.match.exclusions.length;
    });

    /* Les packs sortent du classement principal. Sans cela, le Pack
       Aquatique et ses trois composants occupent les quatre premières
       places, ce qui n'informe personne. */
    var packs = live.filter(isPack);
    var single = live.filter(function (r) { return !isPack(r); });

    var best = single.filter(function (r) { return r.match.rawScore >= 60 && isRecommendable(r); });
    var others = single.filter(function (r) { return r.match.rawScore < 60 || !isRecommendable(r); });
    var excluded = scored.filter(function (r) { return r.match.exclusions.length; });
    var unscored = scored.filter(function (r) { return r.match.rawScore == null; });

    /* Le classement se fait sur `rawScore`, jamais sur ce qui est
       affiché : un produit bien documenté ne doit pas passer devant un
       meilleur produit au seul motif qu'il montre un chiffre.

       À score égal, la confiance départage, puis la robustesse — c'est
       le seul rôle de `durability_score`, trop corrélé à la mastication
       pour entrer dans le calcul. */
    var bySore = function (a, b) {
      if (b.match.rawScore !== a.match.rawScore) return b.match.rawScore - a.match.rawScore;
      if (b.match.confidence !== a.match.confidence) return b.match.confidence - a.match.confidence;
      return (b.product.durability || 0) - (a.product.durability || 0);
    };
    best.sort(bySore);
    others.sort(bySore);
    packs.sort(bySore);

    return { best: best, others: others, packs: packs, excluded: excluded, unscored: unscored };
  }

  /* Un pack vaut son maillon le plus faible pour la sécurité, et la
     moyenne de ses composants pour la pertinence. Recommander un pack
     dont un seul article est dangereux serait une faute. */
  function matchPack(dog, pack, componentMatches) {
    if (!componentMatches || !componentMatches.length) return calculateMatch(dog, pack);
    var excl = [];
    var sum = 0, n = 0, conf = 0, assumed = false;
    componentMatches.forEach(function (m) {
      if (m.exclusions.length) excl = excl.concat(m.exclusions);
      if (m.assumed) assumed = true;
      if (m.rawScore != null) { sum += m.rawScore; n++; conf += m.confidence; }
    });
    if (!n) {
      return {
        rawScore: null, level: null, confidence: 0, assumed: assumed,
        presentation: { mode: 'none', publicScore: null, label: null },
        matched: [], warnings: [], exclusions: excl
      };
    }
    var avg = Math.round(sum / n);
    var rawScore = excl.length ? Math.min(avg, 35) : avg;
    var level = levelFor(rawScore);
    /* Un pack ne peut pas être mieux documenté que le moins documenté de
       ses articles : la confiance est la moyenne, et une présomption sur
       un seul composant suffit à retirer le pourcentage. */
    var confidence = Math.round((conf / n) * 100) / 100;
    return {
      rawScore: rawScore,
      level: level,
      confidence: confidence,
      assumed: assumed,
      presentation: presentationFor(rawScore, confidence, level, assumed, excl.length > 0),
      matched: [],
      warnings: excl.length ? ['Un article de ce pack ne convient pas à son profil.'] : [],
      exclusions: excl
    };
  }

  function num(v) {
    if (v === '' || v == null) return null;
    var n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
    return isFinite(n) ? n : null;
  }

  function clamp(v, lo, hi) {
    if (v == null) return null;
    return Math.max(lo, Math.min(hi, v));
  }

  return {
    SIZE_BANDS: SIZE_BANDS,
    AGE_BANDS: AGE_BANDS,
    WEIGHTS: WEIGHTS,
    deburr: deburr,
    levelFromLabel: levelFromLabel,
    parseWeightRange: parseWeightRange,
    sizeFromWeight: sizeFromWeight,
    lifeStageFromMonths: lifeStageFromMonths,
    normalizeBreed: normalizeBreed,
    normalizeDogProfile: normalizeDogProfile,
    normalizeProduct: normalizeProduct,
    attachBreedRecommendations: attachBreedRecommendations,
    calculateMatch: calculateMatch,
    rankProducts: rankProducts,
    matchPack: matchPack,
    levelFor: levelFor,
    presentationFor: presentationFor
  };
});
